import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ClosableModal,
  SecondaryButton,
  PrimaryLoadingButton,
  IconLoadingButton,
} from 'components';
import { ExpertContent } from 'pages/media-library/interface';
import { mediaLibraryAction } from 'pages/media-library/slice';

import { FileIcon, FolderIcon } from 'assets/icons';
import { Box, CircularProgress, Typography } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowRight, MoveDown } from '@mui/icons-material';
import {
  selectExpandedMediaItems,
  selectIsDownloadMediaEnabled,
  selectMediaLoading, selectMediaWithSubFolders,
  selectSaveAndShareRecording, selectSelectedFiles, selectSelectedTargetFolder,
  selectUndoButtonDisabled,
} from 'pages/media-library/selector';
import { useEffect } from 'react';
import { FileType } from 'utils/file-uploader';

const RootFolderComponent = ({
  path, title, _id, subFolders, hasSubFolders, fileType,
}: ExpertContent) => {
  const dispatch = useDispatch();
  const targetFolder = useSelector(selectSelectedTargetFolder);
  const isExpanded = useSelector(selectExpandedMediaItems).includes(_id);

  const handleClick = () => {
    if (hasSubFolders) {
      dispatch(mediaLibraryAction.toggleExpanded(_id));
    }
    dispatch(mediaLibraryAction.setSelectedTargetFolder({
      id: _id ?? '',
      path: path ?? '',
      title: title ?? '',
    }));
    if (path === '/root' && hasSubFolders && !subFolders) {
      dispatch(mediaLibraryAction.fetchFolderSelectorMedia({
        parentFolderId: _id,
        folderPath: `${path}/${title}`,
      }));
    }
  };

  const isFolderSelected = targetFolder?.id === _id;
  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'start',
          justifyContent: 'start',
          cursor: 'pointer',
          width: '100%',
          paddingTop: '4px',
          paddingLeft: '15px',
          background: isFolderSelected ? '#676b6e45' : '',
        }}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={() => {}}
      >
        <div
          style={{
            width: '15px', display: 'flex', justifyContent: 'center', paddingRight: '1px',
          }}
        >
          {(hasSubFolders) && (
            (isExpanded && subFolders) ? <KeyboardArrowDown fontSize="small" />
              : <KeyboardArrowRight fontSize="small" />
          )}
        </div>
        {fileType === FileType.FOLDER
          ? <FolderIcon sx={{ mr: 1, color: (t) => t.palette.primary.main }} />
          : <FileIcon sx={{ mr: 1, color: (t) => t.palette.primary.main }} />}
        <Typography variant="body2">
          {title}
        </Typography>
      </div>
      {isExpanded && subFolders && subFolders.length > 0 && (
        subFolders.map((subFolder) => (
          <div key={subFolder._id} style={{ paddingLeft: '15px', width: '100%' }}>
            <RootFolderComponent {...subFolder} />
          </div>
        ))
      )}
    </>
  );
};

const FolderTreeComponent = () => {
  const media = useSelector(selectMediaWithSubFolders);
  const selectedTargetFolder = useSelector(selectSelectedTargetFolder);
  const dispatch = useDispatch();
  const [isHomeExpanded, setIsHomeExpanded] = React.useState(true);

  const HandleHomeClick = () => {
    dispatch(mediaLibraryAction.setSelectedTargetFolder({
      id: '',
      path: '/root',
      title: '',
    }));
    setIsHomeExpanded(!isHomeExpanded);
  };

  const renderSpecialFolder = (
    name: string,
    isSelected: boolean,
    onClick: () => void,
    isExpandable = true,
    isExpanded = false,
  ) => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        cursor: 'pointer',
        width: '100%',
        paddingTop: '4px',
        background: isSelected ? '#676b6e45' : '#f0f0f0',
      }}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={() => {}}
    >
      {isExpandable && (
      <div style={{
        width: '15px', display: 'flex', justifyContent: 'center', paddingRight: '1px',
      }}
      >
        {isExpanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
      </div>
      )}
      <FolderIcon sx={{ mr: 1, color: (t) => t.palette.primary.main }} />
      <Typography variant="body2">
        {name}
      </Typography>
    </div>
  );

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'start',
      justifyContent: 'start',
      width: '100%',
      paddingLeft: '30px',
    }}
    >
      {renderSpecialFolder('Home', selectedTargetFolder?.id === '', HandleHomeClick, true, isHomeExpanded)}
      {isHomeExpanded && media.map((folder) => (
        <RootFolderComponent {...folder} />
      ))}

    </div>
  );
};

interface Props {
  open: boolean;
  onClose: () => void;
  // eslint-disable-next-line react/require-default-props
  handleSaveToMedia?: (isDownload: boolean) => void;
  // eslint-disable-next-line react/require-default-props
  handleSaveAndShareRecording?: (isDownload: boolean) => void;
}

const SelectFolderModel = ({
  open, onClose, handleSaveToMedia, handleSaveAndShareRecording,
}: Props) => {
  const dispatch = useDispatch();
  const selectedTargetFolder = useSelector(selectSelectedTargetFolder);
  const loading = useSelector(selectMediaLoading);
  const undoButtonDisabled = useSelector(selectUndoButtonDisabled);
  const selectedMedia = useSelector(selectSelectedFiles);
  const isDownloadMediaEnabled = useSelector(selectIsDownloadMediaEnabled);
  const isSaveAndShareRecording = useSelector(selectSaveAndShareRecording);

  useEffect(() => {
    dispatch(mediaLibraryAction.fetchFolderSelectorMedia());
  }, [dispatch]);

  const [buttonDisabled, setButtonDisabled] = React.useState(false);
  const isSelectedMediaEmpty = selectedMedia.length === 0;

  const handleMoveFile = () => {
    dispatch(mediaLibraryAction.moveMedia());
  };

  const handleUndoButtonClick = () => {
    dispatch(mediaLibraryAction.undoMediaMove());
  };

  const handelSubmit = () => {
    if (handleSaveToMedia) {
      handleSaveToMedia(isDownloadMediaEnabled ?? false);
      dispatch(mediaLibraryAction.setOpenFolderSelect(false));
    } else if (handleSaveAndShareRecording) {
      handleSaveAndShareRecording(isSaveAndShareRecording ?? false);
    } else {
      handleMoveFile();
    }
  };

  const subTitle = React.useMemo(() => {
    if (handleSaveAndShareRecording) return 'Select a folder to save your recording';
    if (handleSaveToMedia) return 'Select a folder to save your media';
    return 'Select a folder to move your media';
  }, [handleSaveAndShareRecording, handleSaveToMedia]);

  const submitButtonText = React.useMemo(() => {
    if (handleSaveToMedia) {
      return 'Save to media';
    }
    if (handleSaveAndShareRecording) {
      return isSaveAndShareRecording === true ? 'Save and share recording' : 'Save Recording';
    }
    return 'Move Files';
  }, [handleSaveAndShareRecording, handleSaveToMedia, isSaveAndShareRecording]);

  return (
    <ClosableModal
      open={open}
      onClose={onClose}
      title="Select Folder"
      subTitle={subTitle}
    >
      {!(handleSaveToMedia || handleSaveAndShareRecording)
      && (
      <IconLoadingButton
        className="w-auto"
        disabled={undoButtonDisabled}
        onClick={handleUndoButtonClick}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'start',
          justifyContent: 'start',
          gap: '5px',
        }}
        >
          <MoveDown />
          <span>Move selected media back to the home page/folder</span>
        </div>

      </IconLoadingButton>
      )}

      <div style={{
        minWidth: '200px', width: '100%', height: '350px', overflow: 'auto', background: '#f0f0f0', marginBottom: '25px', marginTop: '10px',
      }}
      >
        {loading ? (
          <Box sx={{ pt: 2 }} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'start',
              paddingLeft: '5px',
              paddingTop: '2px',
              background: '#f0f0f0',
            }}
          >
            <FolderTreeComponent />
          </div>
        )}

      </div>

      <div className="row">
        <div className="col-12 col-md-6 mb-2 mb-md-0">
          <SecondaryButton fullWidth onClick={onClose}>
            Cancel
          </SecondaryButton>
        </div>
        <div className="col-12 col-md-6">
          <PrimaryLoadingButton
            fullWidth
            loading={loading}
            onClick={handelSubmit}
            disabled={loading || buttonDisabled
              || selectedTargetFolder === null
              || (isSelectedMediaEmpty && !handleSaveToMedia && !handleSaveAndShareRecording)}
          >
            {submitButtonText}
          </PrimaryLoadingButton>
        </div>
      </div>
    </ClosableModal>
  );
};

export default React.memo(SelectFolderModel);
